import React, {Fragment} from "react";
import {FileDownload, OpenInNew} from "@mui/icons-material";
import {Box, Divider, Grid, IconButton, Tooltip} from "@mui/material";
import moment from "moment";
import {Trans, useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {getResourceDownloadUrl} from "../../constants/apiEndpoints";
import OdataUrlViewer from "../odata-url-viewer";
import SanitizedHTML from "../sanitized-html";
import {appConfigDateFormatSelector, appConfigSelector} from "../../state/app-config/appConfigSelectors";
import {catalogSelector} from "../../state/catalog/catalogSelectors";
import {configSelector} from "../../state/config/configSelectors";
import {fileSave} from "../../state/file-save/fileSaveActions";
import {packageDownloaded} from "../../state/miscellaneous/miscellaneousActions";
import {nodeIdSelector} from "../../state/node/nodeSelectors";
import {CATALOG_RESOURCE_TYPE_ODATA, getResourceFromCatalogById} from "../../utils/catalog";
import {isValidUrl} from "../../utils/urls";

const rowProps = {
  className: "metadata-json-viewer__row",
  size: 12,
  sx: {
    padding: "8px",
    borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
    "&:nth-of-type(odd)": {
      backgroundColor: "#f5f5f5"
    }
  }
};

const leftColProps = {
  className: "metadata-json-viewer__row__left-col",
  size: {
    xs: 12,
    sm: 4,
    lg: 3
  },
  sx: {
    padding: "0 4px",
    fontWeight: "bold"
  }
};

const rigthColProps = {
  className: "metadata-json-viewer__row__right-col",
  size: {
    xs: 12,
    sm: 8,
    lg: 9
  },
  sx: {
    padding: "0 4px",
    wordBreak: "break-word",
    "& .metadata-json-viewer__row__right-col__label": {
      marginRight: "4px",
      fontStyle: "italic"
    },
    "& .metadata-json-viewer__row__right-col__value.sanitized-html": {
      display: "inline",
      whiteSpace: "pre-line"
    },
    "& .metadata-json-viewer__row__right-col__value > p": {
      margin: 0,
      padding: 0,
      border: 0,
      font: "inherit",
      color: "inherit",
      background: "transparent",
      display: "inline",
      lineHeight: "normal",
      verticalAlign: "baseline",
      textAlign: "inherit"
    },
    "& .metadata-json-viewer__divider": {
      margin: "8px 0"
    }
  }
};

const MetadataSimpleField = ({label, value, link, id, i18nPrefix}) => {
  const {t, i18n} = useTranslation();
  const i18nKey = i18nPrefix ? `${i18nPrefix}.${value}` : null;
  const translatedLabel = i18nKey && i18n.exists(i18nKey) ? t(i18nKey) : value;

  return (
    value && (
      <Grid container id={id} {...rowProps}>
        <Grid {...leftColProps}>{label}</Grid>
        <Grid {...rigthColProps}>
          {link ? (
            <span className="metadata-json-viewer__row__right-col__value metadata-json-viewer__simple-field__link">
              <a href={link}>{value}</a>
            </span>
          ) : (
            <SanitizedHTML
              html={translatedLabel}
              allowTarget
              className="metadata-json-viewer__row__right-col__value metadata-json-viewer__simple-field__value"
            />
          )}
        </Grid>
      </Grid>
    )
  );
};

const MetadataAgentListField = ({label, agents: _agents, id}) => {
  const {t} = useTranslation();

  const agents = (_agents ?? []).filter(i => i?.name || i?.identifier);

  return (
    agents.length > 0 && (
      <Grid container id={id} {...rowProps}>
        <Grid {...leftColProps}>{label}</Grid>
        <Grid {...rigthColProps}>
          {agents.map(({name, identifier}, idx) => (
            <Fragment key={idx}>
              {name && identifier ? (
                <>
                  {name && (
                    <span style={{display: "block"}} className="metadata-json-viewer__agent-field__name">
                      <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__agent-field__name__label">
                        {t("components.metadataJsonViewer.agentField.name")}:
                      </span>
                      <SanitizedHTML
                        html={name}
                        allowTarget
                        className="metadata-json-viewer__row__right-col__value metadata-json-viewer__agent-field__name__value"
                      />
                    </span>
                  )}
                  {identifier && (
                    <span style={{display: "block"}} className="metadata-json-viewer__agent-field__identifier">
                      <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__agent-field__identifier__label">
                        {t("components.metadataJsonViewer.agentField.identifier")}:
                      </span>
                      <SanitizedHTML
                        html={identifier}
                        allowTarget
                        className="metadata-json-viewer__row__right-col__value metadata-json-viewer__agent-field__identifier__value"
                      />
                    </span>
                  )}
                </>
              ) : (
                <SanitizedHTML
                  html={name ?? identifier}
                  allowTarget
                  className="metadata-json-viewer__row__right-col__value metadata-json-viewer__agent-field__value"
                />
              )}
              {idx < agents.length - 1 && <Divider className="metadata-json-viewer__divider" />}
            </Fragment>
          ))}
        </Grid>
      </Grid>
    )
  );
};

const MetadataContactPointsField = ({label, contactPoints: _containctPoints, id}) => {
  const {t} = useTranslation();

  const contactPoints = (_containctPoints ?? []).filter(i => i?.name || i?.phone || i?.email || i?.website);

  return (
    contactPoints.length > 0 && (
      <Grid container id={id} {...rowProps}>
        <Grid {...leftColProps}>{label}</Grid>
        <Grid {...rigthColProps}>
          {contactPoints.map(({name, phone, email, website}, idx) => (
            <Fragment key={idx}>
              {name && (
                <span style={{display: "block"}} className="metadata-json-viewer__contact-field__name">
                  <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__contact-field__name__label">
                    {t("components.metadataJsonViewer.contactsField.name")}:
                  </span>
                  <SanitizedHTML
                    html={name}
                    allowTarget
                    className="metadata-json-viewer__row__right-col__value metadata-json-viewer__contact-field__name__value"
                  />
                </span>
              )}
              {phone && (
                <span style={{display: "block"}} className="metadata-json-viewer__contact-field__phone">
                  <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__contact-field__phone__label">
                    {t("components.metadataJsonViewer.contactsField.phone")}:
                  </span>
                  <SanitizedHTML
                    html={phone}
                    allowTarget
                    className="metadata-json-viewer__row__right-col__value metadata-json-viewer__contact-field__phone__value"
                  />
                </span>
              )}
              {email && (
                <span style={{display: "block"}} className="metadata-json-viewer__contact-field__email">
                  <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__contact-field__email__label">
                    {t("components.metadataJsonViewer.contactsField.email")}:
                  </span>
                  {/* <a href={`mailto:${email}`}>{email}</a> */}
                  <SanitizedHTML
                    html={email}
                    allowTarget
                    className="metadata-json-viewer__row__right-col__value metadata-json-viewer__contact-field__email__value"
                  />
                </span>
              )}
              {website && (
                <span style={{display: "block"}} className="metadata-json-viewer__contact-field__website">
                  <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__contact-field__website__label">
                    {t("components.metadataJsonViewer.contactsField.website")}:
                  </span>
                  <span className="metadata-json-viewer__row__right-col__value metadata-json-viewer__contact-field__website__value">
                    <a href={website}>{website}</a>
                  </span>
                </span>
              )}
              {idx < contactPoints.length - 1 && <Divider className="metadata-json-viewer__divider" />}
            </Fragment>
          ))}
        </Grid>
      </Grid>
    )
  );
};

const MetadataTemporalCoverageField = ({label, temporalCoverage: _temporalCoverage, id}) => {
  const {t} = useTranslation();
  const dateFormt = useSelector(appConfigDateFormatSelector);

  const temporalCoverage = (_temporalCoverage ?? []).filter(i => i?.from || i?.to);

  return (
    temporalCoverage.length > 0 && (
      <Grid container id={id} {...rowProps}>
        <Grid {...leftColProps}>{label}</Grid>
        {temporalCoverage.map(({from, to}, idx) => (
          <Grid key={idx} {...rigthColProps}>
            <span style={{display: "flex", gap: "8px"}}>
              <span className="metadata-json-viewer__from-to-field__from">
                <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__from-to-field__from__label">
                  {t("components.metadataJsonViewer.fromToField.from")}:
                </span>
                <span className="metadata-json-viewer__row__right-col__value metadata-json-viewer__from-to-field__from__value">
                  {from ? moment(from).format(dateFormt) : "-"}
                </span>
              </span>
              <span className="metadata-json-viewer__from-to-field__to">
                <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__from-to-field__to__label">
                  {t("components.metadataJsonViewer.fromToField.to")}:
                </span>
                <span className="metadata-json-viewer__row__right-col__value metadata-json-viewer__from-to-field__to__value">
                  {to ? moment(to).format(dateFormt) : "-"}
                </span>
              </span>
            </span>
            {idx < temporalCoverage.length - 1 && <Divider className="metadata-json-viewer__divider" />}
          </Grid>
        ))}
      </Grid>
    )
  );
};

const MetadataTreeField = ({label, tree}) => {
  return (
    (tree || []).length > 0 && (
      <Grid container {...rowProps}>
        <Grid {...leftColProps}>{label}</Grid>
        <Grid {...rigthColProps}>
          <RecursiveList nodes={tree} />
        </Grid>
      </Grid>
    )
  );
};

const RecursiveList = ({nodes, level = 0}) => {
  return (nodes || []).map(node => (
    <span key={node.identifier} style={{display: "block", paddingLeft: `${level * 8}px`}}>
      <SanitizedHTML
        html={node.identifier}
        allowTarget
        className={`metadata-json-viewer__row__right-col__value metadata-json-viewer__recursive-field__${level}`}
      />
      {(node.children || []).length > 0 && <RecursiveList nodes={node.children} level={level + 1} />}
    </span>
  ));
};

const MetadataDistributionsField = ({label, distributions: _distributions, id}) => {
  const {t} = useTranslation();
  const dateFormat = useSelector(appConfigDateFormatSelector);

  const distributions = (_distributions ?? []).filter(
    i =>
      i?.title ||
      i?.description ||
      i?.format ||
      i?.accessUrl ||
      i?.size ||
      i?.lastModified ||
      i?.downloadUrl ||
      i?.license?.name ||
      i?.license?.type ||
      i?.license?.version
  );

  return (
    distributions.length > 0 && (
      <Grid container id={id} {...rowProps}>
        <Grid {...leftColProps}>{label}</Grid>
        {distributions.map(({title, description, format, accessUrl, size, lastModified, downloadUrl, license}, idx) => (
          <Grid key={idx} {...rigthColProps}>
            {title && (
              <span style={{display: "block"}} className="metadata-json-viewer__distribution__title">
                <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__distribution__title__label">
                  {t("components.metadataJsonViewer.distribution.title")}:
                </span>
                <SanitizedHTML
                  html={title}
                  allowTarget
                  className="metadata-json-viewer__row__right-col__value metadata-json-viewer__distribution__title__value"
                />
              </span>
            )}
            {description && (
              <span style={{display: "block"}} className="metadata-json-viewer__distribution__description">
                <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__distribution__description__label">
                  {t("components.metadataJsonViewer.distribution.description")}:
                </span>
                <SanitizedHTML
                  html={description}
                  allowTarget
                  className="metadata-json-viewer__row__right-col__value metadata-json-viewer__distribution__description__value"
                />
              </span>
            )}
            {format && (
              <span style={{display: "block"}} className="metadata-json-viewer__distribution__format">
                <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__distribution__format__label">
                  {t("components.metadataJsonViewer.distribution.format")}:
                </span>
                <SanitizedHTML
                  html={format}
                  allowTarget
                  className="metadata-json-viewer__row__right-col__value metadata-json-viewer__distribution__format__value"
                />
              </span>
            )}
            {accessUrl && (
              <span style={{display: "block"}} className="metadata-json-viewer__distribution__accessUrl">
                <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__distribution__access-url__label">
                  {t("components.metadataJsonViewer.distribution.accessUrl")}:
                </span>
                <span className="metadata-json-viewer__row__right-col__value metadata-json-viewer__distribution__access-url__value">
                  <a href={accessUrl}>{accessUrl}</a>
                </span>
              </span>
            )}
            {size && (
              <span style={{display: "block"}} className="metadata-json-viewer__distribution__size">
                <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__distribution__size__label">
                  {t("components.metadataJsonViewer.distribution.size")}:
                </span>
                <SanitizedHTML
                  html={size}
                  allowTarget
                  className="metadata-json-viewer__row__right-col__value metadata-json-viewer__distribution__size__value"
                />
              </span>
            )}
            {lastModified && (
              <span style={{display: "block"}} className="metadata-json-viewer__distribution__lastModified">
                <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__distribution__last-modified__label">
                  {t("components.metadataJsonViewer.distribution.lastModified")}:
                </span>
                <span className="metadata-json-viewer__row__right-col__value metadata-json-viewer__distribution__last-modified__value">
                  {moment(lastModified).format(dateFormat)}
                </span>
              </span>
            )}
            {downloadUrl && (
              <span style={{display: "block"}} className="metadata-json-viewer__distribution__downloadUrl">
                <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__distribution__download-url__label">
                  {t("components.metadataJsonViewer.distribution.downloadUrl")}:
                </span>
                <span className="metadata-json-viewer__row__right-col__value metadata-json-viewer__distribution__download-url__value">
                  <a href={downloadUrl}>{downloadUrl}</a>
                </span>
              </span>
            )}
            {license?.name && (
              <span style={{display: "block"}} className="metadata-json-viewer__distribution__licenseName">
                <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__distribution__license-name__label">
                  {t("components.metadataJsonViewer.distribution.licenseName")}:
                </span>
                <SanitizedHTML
                  html={license.name}
                  allowTarget
                  className="metadata-json-viewer__row__right-col__value metadata-json-viewer__distribution__license-name__value"
                />
              </span>
            )}
            {license?.type && (
              <span style={{display: "block"}} className="metadata-json-viewer__distribution__licenseType">
                <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__distribution__license-type__label">
                  {t("components.metadataJsonViewer.distribution.licenseType")}:
                </span>
                <SanitizedHTML
                  html={license.type}
                  allowTarget
                  className="metadata-json-viewer__row__right-col__value metadata-json-viewer__distribution__license-type__value"
                />
              </span>
            )}
            {license?.version && (
              <span style={{display: "block"}} className="metadata-json-viewer__distribution__licenseVersion">
                <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__distribution__license-version__label">
                  {t("components.metadataJsonViewer.distribution.licenseVersion")}:
                </span>
                <SanitizedHTML
                  html={license.version}
                  allowTarget
                  className="metadata-json-viewer__row__right-col__value metadata-json-viewer__distribution__license-version__value"
                />
              </span>
            )}
            {idx < distributions.length - 1 && <Divider className="metadata-json-viewer__divider" />}
          </Grid>
        ))}
      </Grid>
    )
  );
};

const MetadataConformsToField = ({label, conformsTo: _conformsTo, id}) => {
  const {t} = useTranslation();

  const conformsTo = (_conformsTo ?? []).filter(
    i => i?.identifier || i?.title || i?.description || (i?.referenceDocumentation || []).length > 0
  );

  return (
    conformsTo.length > 0 && (
      <Grid container id={id} {...rowProps}>
        <Grid {...leftColProps}>{label}</Grid>
        {conformsTo.map(({identifier, title, description, referenceDocumentation}, idx) => (
          <Grid key={idx} {...rigthColProps}>
            {identifier && (
              <span style={{display: "block"}} className="metadata-json-viewer__conforms-to__identifier">
                <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__conforms-to__identifier__label">
                  {t("components.metadataJsonViewer.conformsTo.identifier")}:
                </span>
                <SanitizedHTML
                  html={identifier}
                  allowTarget
                  className="metadata-json-viewer__row__right-col__value metadata-json-viewer__conforms-to__identifier__value"
                />
              </span>
            )}
            {title && (
              <span style={{display: "block"}} className="metadata-json-viewer__conforms-to__title">
                <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__conforms-to__title__label">
                  {t("components.metadataJsonViewer.conformsTo.title")}:
                </span>
                <SanitizedHTML
                  html={title}
                  allowTarget
                  className="metadata-json-viewer__row__right-col__value metadata-json-viewer__conforms-to__title__value"
                />
              </span>
            )}
            {description && (
              <span style={{display: "block"}} className="metadata-json-viewer__conforms-to__description">
                <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__conforms-to__description__label">
                  {t("components.metadataJsonViewer.conformsTo.description")}:
                </span>
                <SanitizedHTML
                  html={description}
                  allowTarget
                  className="metadata-json-viewer__row__right-col__value metadata-json-viewer__conforms-to__description__value"
                />
              </span>
            )}
            {(referenceDocumentation || []).length > 0 && (
              <span style={{display: "block"}} className="metadata-json-viewer__conforms-to__reference-documentation">
                <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__conforms-to__reference-documentation__label">
                  {t("components.metadataJsonViewer.conformsTo.referenceDocumentation")}:
                </span>
                <span className="metadata-json-viewer__row__right-col__value metadata-json-viewer__conforms-to__reference-documentation__value">
                  {referenceDocumentation.map((refDoc, refDocIdx) => (
                    <Grid key={refDocIdx} container columnSpacing={"4px"} sx={{marginLeft: "4px"}}>
                      <Grid>-</Grid>
                      <Grid>
                        <a href={refDoc} target="_blank" rel="noopener noreferrer">
                          {refDoc}
                        </a>
                      </Grid>
                    </Grid>
                  ))}
                </span>
              </span>
            )}
            {idx < conformsTo.length - 1 && <Divider className="metadata-json-viewer__divider" />}
          </Grid>
        ))}
      </Grid>
    )
  );
};

const MetadataSpatialCoverangeField = ({label, spatialCoverage: _spatialCoverage, id}) => {
  const {t} = useTranslation();

  const spatialCoverage = (_spatialCoverage ?? []).filter(
    i => i?.name || i?.geometry?.type || i?.geometry?.coordiantes || i?.geometry?.crs
  );

  return (
    spatialCoverage.length > 0 && (
      <Grid container id={id} {...rowProps}>
        <Grid {...leftColProps}>{label}</Grid>
        <Grid {...rigthColProps}>
          {spatialCoverage.map(({name, geometry}, idx) => (
            <Fragment key={idx}>
              {name && (
                <span style={{display: "block"}} className="metadata-json-viewer__spatial__name">
                  <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__spatial__name__label">
                    {t("components.metadataJsonViewer.spatial.name")}:
                  </span>
                  <SanitizedHTML
                    html={name}
                    allowTarget
                    className="metadata-json-viewer__row__right-col__value metadata-json-viewer__spatial__name__value"
                  />
                </span>
              )}
              {geometry?.type && (
                <span style={{display: "block"}} className="metadata-json-viewer__spatial__geometry-type">
                  <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__spatial__geometry-type__label">
                    {t("components.metadataJsonViewer.spatial.geometryType")}:
                  </span>
                  <SanitizedHTML
                    html={geometry.type}
                    allowTarget
                    className="metadata-json-viewer__row__right-col__value metadata-json-viewer__spatial__geometry-type__value"
                  />
                </span>
              )}
              {geometry?.coordinates && (
                <span style={{display: "block"}} className="metadata-json-viewer__spatial__geometry-coordinates">
                  <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__spatial__geometry-coordinates__label">
                    {t("components.metadataJsonViewer.spatial.geometryCoordinates")}:
                  </span>
                  <SanitizedHTML
                    html={geometry.coordinates}
                    allowTarget
                    className="metadata-json-viewer__row__right-col__value metadata-json-viewer__spatial__geometry-coordinates__value"
                  />
                </span>
              )}
              {geometry?.crs && (
                <span style={{display: "block"}} className="metadata-json-viewer__spatial__geometry-crs">
                  <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__spatial__geometry-crs__label">
                    {t("components.metadataJsonViewer.spatial.geometryCrs")}:
                  </span>
                  <SanitizedHTML
                    html={geometry.crs}
                    allowTarget
                    className="metadata-json-viewer__row__right-col__value metadata-json-viewer__spatial__geometry-crs__value"
                  />
                </span>
              )}
              {idx < spatialCoverage.length - 1 && <Divider className="metadata-json-viewer__divider" />}
            </Fragment>
          ))}
        </Grid>
      </Grid>
    )
  );
};

const MetadataLanguagesField = ({label, languages, id}) => {
  return (
    languages && (
      <Grid container id={id} {...rowProps}>
        <Grid {...leftColProps}>{label}</Grid>
        <Grid {...rigthColProps}>
          {languages.map((lang, idx) => (
            <Fragment key={idx}>
              <span className="metadata-json-viewer__row__right-col__value metadata-json-viewer__languages-field__value">
                <Trans i18nKey={`metadata.languages.${lang}`} defaults={lang} />
              </span>
              {idx < languages.length - 1 && <span style={{marginRight: "4px"}}>,</span>}
            </Fragment>
          ))}
        </Grid>
      </Grid>
    )
  );
};

const MetadataResourcesField = ({label, value: resources, id, packageId}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const dateFormat = useSelector(appConfigDateFormatSelector);
  const appConfig = useSelector(appConfigSelector);
  const config = useSelector(configSelector);
  const nodeId = useSelector(nodeIdSelector);
  const catalog = useSelector(catalogSelector);
  return (
    (resources || []).length > 0 && (
      <Grid container id={id} {...rowProps}>
        <Grid {...leftColProps}>{label}</Grid>
        <Grid {...rigthColProps}>
          {resources.map(({id, title, description, type, license, lastUpdate, url, isDownloadable}, idx) => (
            <Box key={idx}>
              <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                <Box>
                  {title && (
                    <span style={{display: "block"}} className="metadata-json-viewer__resources__title">
                      <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__resources__title__label">
                        {t("components.metadataJsonViewer.resources.title")}:
                      </span>
                      <SanitizedHTML
                        html={title}
                        allowTarget
                        className="metadata-json-viewer__row__right-col__value metadata-json-viewer__resources__title__value"
                      />
                    </span>
                  )}
                  {description && (
                    <span style={{display: "block"}} className="metadata-json-viewer__resources__description">
                      <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__resources__description__label">
                        {t("components.metadataJsonViewer.resources.description")}:
                      </span>
                      <SanitizedHTML
                        html={description}
                        allowTarget
                        className="metadata-json-viewer__row__right-col__value metadata-json-viewer__resources__description__value"
                      />
                    </span>
                  )}
                  {type && (
                    <span style={{display: "block"}} className="metadata-json-viewer__resources__type">
                      <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__resources__type__label">
                        {t("components.metadataJsonViewer.resources.type")}:
                      </span>
                      <SanitizedHTML
                        html={type}
                        allowTarget
                        className="metadata-json-viewer__row__right-col__value metadata-json-viewer__resources__type__value"
                      />
                    </span>
                  )}
                  {license && (
                    <span style={{display: "block"}} className="metadata-json-viewer__resources__license">
                      <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__resources__license__label">
                        {t("components.metadataJsonViewer.resources.license")}:
                      </span>
                      {isValidUrl(license) ? (
                        <span className="metadata-json-viewer__row__right-col__value metadata-json-viewer__resources__license__url">
                          <a href={license}>{license}</a>
                        </span>
                      ) : (
                        <SanitizedHTML
                          html={license}
                          allowTarget
                          className="metadata-json-viewer__row__right-col__value metadata-json-viewer__resources__license__value"
                        />
                      )}
                    </span>
                  )}
                  {lastUpdate && (
                    <span style={{display: "block"}} className="metadata-json-viewer__resources__last-update">
                      <span className="metadata-json-viewer__row__right-col__label metadata-json-viewer__resources__last-update__label">
                        {t("components.metadataJsonViewer.resources.lastUpdate")}:
                      </span>
                      <span className="metadata-json-viewer__row__right-col__value metadata-json-viewer__resources__last-update__value">
                        {moment(lastUpdate).format(dateFormat)}
                      </span>
                    </span>
                  )}
                </Box>
                {isDownloadable && (
                  <Box sx={{padding: "8px"}}>
                    <Tooltip title={t("components.metadataJsonViewer.resources.download")}>
                      <IconButton
                        onClick={() => {
                          dispatch(packageDownloaded(type, packageId));
                          if (url) {
                            const target =
                              !appConfig.newTabFormats || appConfig.newTabFormats.includes(type) ? "_blank" : "_self";
                            window.open(url, target);
                          } else {
                            dispatch(
                              fileSave(
                                getResourceDownloadUrl(nodeId, packageId, id),
                                {name: title, extension: type},
                                config.baseURL
                              )
                            );
                          }
                        }}
                      >
                        {type === "html" ? (
                          <OpenInNew className={"package-attachments__menu__item__icon"} />
                        ) : (
                          <FileDownload className={"package-attachments__menu__item__icon"} />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                {type === CATALOG_RESOURCE_TYPE_ODATA && (
                  <Box sx={{padding: "8px"}}>
                    <OdataUrlViewer resource={getResourceFromCatalogById(catalog, id)} />
                  </Box>
                )}
              </Box>
              {idx < resources.length - 1 && <Divider className="metadata-json-viewer__divider" />}
            </Box>
          ))}
        </Grid>
      </Grid>
    )
  );
};

const MetadataJsonViewer = ({metadata, notes, packageId}) => {
  const {t} = useTranslation();
  return (
    metadata && (
      <Grid
        container
        id="metadata-json-viewer__container"
        sx={{
          border: "1px solid rgba(0, 0, 0, 0.2)"
        }}
      >
        <MetadataSimpleField
          label={t("components.metadataJsonViewer.labels.title")}
          value={metadata.title}
          id="metadata-json-viewer__title"
        />
        {notes && (
          <MetadataSimpleField
            label={t("components.metadataJsonViewer.labels.notes")}
            value={notes}
            id="metadata-json-viewer__notes"
          />
        )}
        <MetadataSimpleField
          label={t("components.metadataJsonViewer.labels.description")}
          value={metadata.description}
          id="metadata-json-viewer__description"
        />
        <MetadataTreeField
          label={t("components.metadataJsonViewer.labels.themes")}
          tree={metadata.theme}
          id="metadata-json-viewer__theme"
        />
        <MetadataSimpleField
          label={t("components.metadataJsonViewer.labels.keywords")}
          value={metadata.keywords}
          id="metadata-json-viewer__keywords"
        />
        <MetadataSimpleField
          label={t("components.metadataJsonViewer.labels.source")}
          value={metadata.source}
          id="metadata-json-viewer__source"
        />
        <MetadataAgentListField
          label={t("components.metadataJsonViewer.labels.creators")}
          agents={metadata.creators}
          id="metadata-json-viewer__creators"
        />
        <MetadataAgentListField
          label={t("components.metadataJsonViewer.labels.rightsHolder")}
          agents={[metadata.rightsHolder]}
          id="metadata-json-viewer__rights-holder"
        />
        <MetadataAgentListField
          label={t("components.metadataJsonViewer.labels.publisher")}
          agents={[metadata.publisher]}
          id="metadata-json-viewer__publisher"
        />
        <MetadataSimpleField
          label={t("components.metadataJsonViewer.labels.issued")}
          value={metadata.issued}
          id="metadata-json-viewer__issued"
        />
        <MetadataSimpleField
          label={t("components.metadataJsonViewer.labels.modified")}
          value={metadata.modified}
          id="metadata-json-viewer__modified"
        />
        <MetadataSimpleField
          label={t("components.metadataJsonViewer.labels.accrualPeriodicity")}
          value={metadata.accrualPeriodicity}
          id="metadata-json-viewer__accrual-periodicity"
          i18nPrefix="metadata.frequencies"
        />
        <MetadataLanguagesField
          label={t("components.metadataJsonViewer.labels.languages")}
          languages={metadata.languages}
          id="metadata-json-viewer__language"
        />
        <MetadataTemporalCoverageField
          label={t("components.metadataJsonViewer.labels.temporalCoverage")}
          temporalCoverage={metadata.temporalCoverage}
          id="metadata-json-viewer__temporal-coverage"
        />
        <MetadataSpatialCoverangeField
          label={t("components.metadataJsonViewer.labels.spatialCoverage")}
          spatialCoverage={metadata.spatialCoverage}
          id="metadata-json-viewer__conforms-to"
        />
        <MetadataSimpleField
          label={t("components.metadataJsonViewer.labels.versionOf")}
          value={metadata.versionOf}
          link={metadata.versionOf}
          id="metadata-json-viewer__version-of"
        />
        <MetadataSimpleField
          label={t("components.metadataJsonViewer.labels.versionInfo")}
          value={metadata.versionInfo}
          id="metadata-json-viewer__version-info"
        />
        <MetadataSimpleField
          label={t("components.metadataJsonViewer.labels.landingPage")}
          value={metadata.landingPage}
          link={metadata.landingPage}
          id="metadata-json-viewer__landing-page"
        />
        <MetadataContactPointsField
          label={t("components.metadataJsonViewer.labels.contactPoints")}
          contactPoints={metadata.contactPoints}
          id="metadata-json-viewer__contact-points"
        />
        <MetadataDistributionsField
          label={t("components.metadataJsonViewer.labels.distributions")}
          distributions={metadata.distributions}
          id="metadata-json-viewer__distributions"
        />
        <MetadataConformsToField
          label={t("components.metadataJsonViewer.labels.conformsTo")}
          conformsTo={metadata.conformsTo}
          id="metadata-json-viewer__conforms-to"
        />
        <MetadataSimpleField
          label={t("components.metadataJsonViewer.labels.identifier")}
          value={metadata.identifier}
          id="metadata-json-viewer__identifier"
        />
        <MetadataSimpleField
          label={t("components.metadataJsonViewer.labels.altIdentifier")}
          value={metadata.altIdentifier}
          id="metadata-json-viewer__alt-identifier"
        />
        {typeof metadata.license === "string" ? (
          <MetadataSimpleField
            label={t("components.metadataJsonViewer.labels.license")}
            value={metadata.license}
            id="metadata-json-viewer__license"
          />
        ) : (
          <MetadataTreeField
            label={t("components.metadataJsonViewer.labels.license")}
            tree={metadata.license}
            id="metadata-json-viewer__license"
          />
        )}
        {(metadata.extra || []).map(field => (
          <MetadataSimpleField
            key={field.id}
            label={field.label}
            value={field.value}
            link={field.link}
            id={`metadata-json-viewer__${field.id}`}
          />
        ))}
        <MetadataResourcesField
          packageId={packageId}
          label={t("components.metadataJsonViewer.labels.resources")}
          value={metadata.resources}
          id="metadata-json-viewer__resources"
        />
      </Grid>
    )
  );
};

export default MetadataJsonViewer;
